
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../App.jsx';
import { Star, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';

const TAGS_CONFIG = {
    positive: ["Excelente Pagador", "Cordial e Educado", "Ambiente Organizado", "Comunicação Clara", "Local Seguro", "Cumpre Horários"],
    neutral: ["Bom Pagador", "Respeitoso", "Instruções Claras", "Ambiente Apropriado"],
    negative: ["Atraso no Pagamento", "Comunicação Difícil", "Local de Difícil Acesso", "Instruções Imprecisas", "Ambiente Desorganizado"]
};

// LISTA DE PALAVRAS IMPRÓPRIAS (FILTRO DE CONDUTA)
const BANNED_WORDS = ["porra", "caralho", "puta", "merda", "fdp", "viado", "cu", "desgraça", "lixo", "bosta"];

const RatingScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { db, user, submitRating, markNotificationAsDone, showMessage } = useAppContext();

    const { notification } = location.state || {};
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingExisting, setIsLoadingExisting] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    // Identifica se o alvo da avaliação é o CLIENTE ou o SERVI
    const isEvaluatingClient = notification?.type === 'rating_request_client';
    const targetUserId = isEvaluatingClient ? notification?.relatedClientId : notification?.relatedServiId;
    const targetRole = isEvaluatingClient ? 'client' : 'servi';

    // 1. Busca avaliação anterior para evitar duplicidade (Estilo Google)
    useEffect(() => {
        const checkExistingRating = async () => {
            if (!db || !user || !targetUserId) return;

            try {
                const ratingsRef = collection(db, 'users', targetUserId, 'ratings');
                const q = query(ratingsRef, where("raterId", "==", user.uid), limit(1));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const existingData = querySnapshot.docs[0].data();
                    setRating(existingData.rating);

                    if (isEvaluatingClient) {
                        setSelectedTags(existingData.comment ? existingData.comment.split(', ') : []);
                    } else {
                        setComment(existingData.comment || '');
                    }
                    setIsEditing(true);
                }
            } catch (error) {
                console.error("Erro ao buscar avaliação anterior:", error);
            } finally {
                setIsLoadingExisting(false);
            }
        };

        checkExistingRating();
    }, [db, user, targetUserId, isEvaluatingClient]);

    if (!notification || !targetUserId) {
        return (
            <div className="p-4 flex flex-col items-center justify-center h-full bg-gray-50">
                <p className="text-red-500 font-semibold">Erro: Informações da avaliação não encontradas.</p>
                <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 font-bold">Voltar</button>
            </div>
        );
    }

    const toggleTag = (tag) => {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const getAvailableTags = () => {
        if (rating >= 4) return TAGS_CONFIG.positive;
        if (rating === 3) return TAGS_CONFIG.neutral;
        return TAGS_CONFIG.negative;
    };

    // FUNÇÃO CIRÚRGICA DE FILTRAGEM DE PALAVRAS
    const filterBannedWords = (text) => {
        if (!text) return "";
        let filtered = text;
        BANNED_WORDS.forEach(word => {
            const regex = new RegExp(`\\b${word}\\b`, 'gi');
            filtered = filtered.replace(regex, "****");
        });
        return filtered;
    };

    const handleSubmit = async () => {
        if (rating === 0) {
            showMessage("Selecione pelo menos uma estrela.", "warning");
            return;
        }

        if (isEvaluatingClient && selectedTags.length === 0) {
            showMessage("Selecione pelo menos uma característica.", "warning");
            return;
        }

        setIsSubmitting(true);
        try {
            const finalComment = isEvaluatingClient
                ? selectedTags.join(', ')
                : filterBannedWords(comment);

            await submitRating(targetUserId, targetRole, {
                rating,
                comment: finalComment,
                tags: selectedTags,
                relatedChatId: notification.relatedChatId
            });

            await markNotificationAsDone(notification.id);
            showMessage(isEditing ? "Avaliação atualizada!" : "Avaliação enviada!", "success");
            navigate('/notifications');
        } catch (error) {
            console.error("Erro ao processar avaliação:", error);
            showMessage("Erro ao salvar sua avaliação.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const targetName = notification.title.replace('Avalie o serviço de ', '').replace('Avalie o cliente ', '');

    if (isLoadingExisting) return (
        <div className="h-full flex flex-col items-center justify-center bg-gray-50">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-2" />
            <p className="text-gray-500 font-bold text-xs uppercase">Verificando histórico...</p>
        </div>
    );

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50">
            <div className="flex items-center mb-6">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-2">
                    <ArrowLeft className="w-6 h-6 text-gray-700" />
                </button>
                <h2 className="text-2xl font-bold text-gray-800">
                    {isEditing ? 'Editar Avaliação' : (isEvaluatingClient ? 'Avaliar Cliente' : 'Avaliar Serviço')}
                </h2>
            </div>

            <div className="flex-grow bg-white p-6 rounded-3xl shadow-lg border border-gray-100 overflow-y-auto">
                {isEditing && (
                    <div className="bg-blue-50 text-blue-600 text-[10px] font-black uppercase p-2 rounded-lg mb-4 text-center tracking-widest border border-blue-100">
                        Você já avaliou este {targetRole === 'client' ? 'cliente' : 'profissional'}. Sua nota será atualizada.
                    </div>
                )}

                <p className="text-center text-gray-500 mb-1 text-sm uppercase font-bold tracking-widest">Sua experiência com</p>
                <p className="text-center font-black text-xl text-gray-800 mb-8 leading-tight">{targetName}</p>

                <div className="flex justify-center items-center space-x-2 mb-10">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                            key={star}
                            className={`w-12 h-12 cursor-pointer transition-all active:scale-125 ${rating >= star ? 'text-yellow-400 fill-current' : 'text-gray-200'}`}
                            onClick={() => {
                                setRating(star);
                                if (!isEditing) setSelectedTags([]);
                            }}
                        />
                    ))}
                </div>

                {rating > 0 && (
                    <div className="animate-fade-in">
                        {isEvaluatingClient ? (
                            <div className="space-y-4">
                                <p className="text-sm font-black text-gray-400 uppercase tracking-widest mb-4">Características:</p>
                                <div className="flex flex-wrap gap-2">
                                    {getAvailableTags().map(tag => (
                                        <button
                                            key={tag}
                                            onClick={() => toggleTag(tag)}
                                            className={`px-4 py-2 rounded-full text-xs font-bold transition-all border-2 flex items-center ${
                                                selectedTags.includes(tag)
                                                ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                                                : 'bg-white border-gray-100 text-gray-500 hover:border-blue-200'
                                            }`}
                                        >
                                            {selectedTags.includes(tag) && <Check size={12} className="mr-1.5" />}
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4 text-left">
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Seu depoimento:</p>
                                    <span className={`text-[10px] font-bold ${comment.length >= 280 ? 'text-red-500' : 'text-gray-400'}`}>
                                        {comment.length}/300
                                    </span>
                                </div>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    maxLength={300}
                                    placeholder="Como foi o serviço? Deixe sua opinião..."
                                    className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl h-40 focus:ring-2 focus:ring-blue-400 focus:bg-white focus:outline-none transition-all shadow-inner font-medium text-sm"
                                />
                            </div>
                        )}
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || rating === 0}
                    className="w-full mt-10 bg-blue-600 text-white font-black py-4 rounded-2xl disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-all shadow-xl active:scale-[0.98] uppercase tracking-widest"
                >
                    {isSubmitting ? 'Processando...' : (isEditing ? 'Atualizar Avaliação' : 'Finalizar Avaliação')}
                </button>
            </div>
        </div>
    );
};

export default RatingScreen;
